import { Navigate, useLocation, useRouter } from '@tanstack/react-router';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { BreadcrumbItem } from '@/services/breadcrumbs';
import type { Delivery } from '@/services/db/delivery/entity';
import { getDeliveryState } from '@/services/db/delivery/utils';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';
import Journeys from '@/pages/journeys';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { toHashSuffix } from '@/routes/-journeys-redirects';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import { DeliveryDetailPage } from './delivery-detail';
import OutboxPage from './index.page';
import type { OutboxTab } from './types';

/** The Drafts tab's URL segment — unchanged across both segments. */
const DRAFTS_TAB = 'drafts';

/** The Outbox landing segment — Ember redirects it (and the index) to the default (Sent) tab. */
const WELCOME_SEGMENT = 'welcome';

/**
 * The Outbox's URL segments, each with its "Sent"-tab name and its splat route. The
 * `OutboxSegment` type derives from this catalog, so the segment set is defined in one place.
 */
const OUTBOX_SEGMENTS = {
  outbox: { sentTab: 'deliveries', splatRoute: '/workspaces/$workspaceId/journeys/outbox/$' },
  message_activity: { sentTab: 'sent', splatRoute: '/workspaces/$workspaceId/journeys/message_activity/$' },
} as const satisfies Record<string, { sentTab: string; splatRoute: string }>;

export type OutboxSegment = keyof typeof OUTBOX_SEGMENTS;

/**
 * Where an Outbox splat route is mounted. `outbox` and `message_activity` are the old `journeys`
 * URLs; `hydra` is the page's URL, `/workspaces/:workspaceId/message_activity`, without the
 * `journeys` segment.
 */
export type OutboxMount = OutboxSegment | 'hydra';

/** The Hydra page's splat route. The Sent/Drafts lists and the Sent detail render here. */
const HYDRA_SPLAT_ROUTE = '/workspaces/$workspaceId/message_activity/$';

/** The splat route for paths Ember still serves (the draft detail/edit and anything deeper). */
const EMBER_SPLAT_ROUTE = OUTBOX_SEGMENTS.message_activity.splatRoute;

const MOUNT_SPLAT_ROUTES = {
  outbox: OUTBOX_SEGMENTS.outbox.splatRoute,
  message_activity: OUTBOX_SEGMENTS.message_activity.splatRoute,
  hydra: HYDRA_SPLAT_ROUTE,
} as const satisfies Record<OutboxMount, string>;

/** The URL segment a mount answers to. The Hydra URL uses the `message_activity` spelling. */
function segmentForMount(mount: OutboxMount): OutboxSegment {
  return mount === 'hydra' ? 'message_activity' : mount;
}

/** The route a canonical splat belongs on: Ember for a draft detail, the Hydra page otherwise. */
function splatRouteFor(splat: string) {
  return splat.startsWith(`${DRAFTS_TAB}/`) ? EMBER_SPLAT_ROUTE : HYDRA_SPLAT_ROUTE;
}

/** The URL segment for a logical tab under a given segment. */
function tabSegmentFor(segment: OutboxSegment, tab: OutboxTab): string {
  return tab === 'drafts' ? DRAFTS_TAB : OUTBOX_SEGMENTS[segment].sentTab;
}

/** Every Sent-tab spelling (`deliveries`/`sent`), so a legacy URL is recognized. */
const SENT_TAB_NAMES: ReadonlySet<string> = new Set(Object.values(OUTBOX_SEGMENTS).map((s) => s.sentTab));

/** Collapse leading/trailing/duplicate slashes so a splat compares equal to its canonical form. */
function normalizeSplat(splat: string): string {
  return splat
    .split('/')
    .filter((part) => part.length > 0)
    .join('/');
}

/** What React renders for a splat path: the tab list, a single detail, or an Ember hand-off. */
type OutboxView = { kind: 'ember' } | { kind: 'list'; tab: OutboxTab } | { kind: 'detail'; tab: OutboxTab; id: string };

/** The logical tab a first URL segment names (either Sent-tab spelling → deliveries), or null if none. */
function tabForSegment(first: string): OutboxTab | null {
  if (SENT_TAB_NAMES.has(first)) {
    return 'deliveries';
  }
  return first === DRAFTS_TAB ? 'drafts' : null;
}

/**
 * Classify the splat path. React owns the Deliveries/Sent + Drafts tab lists, the Deliveries/Sent
 * `:id` detail, and the landing paths — the bare index and `/welcome` both resolve to the default
 * (Sent) tab, matching Ember's redirects. Either Sent-tab spelling (`deliveries`/`sent`) is
 * accepted so a cross-rename URL normalizes. The draft detail/edit and any deeper path hand off to
 * the embedded Ember host. The caller redirects any non-canonical result to its canonical URL.
 */
function classifyOutboxPath(splat: string): OutboxView {
  const pathSegments = splat.split('/').filter((part) => part.length > 0);
  const first = pathSegments[0];
  const rest = pathSegments.slice(1);
  // Index and terminal `/welcome` land on the default Sent tab (Ember: index → deliveries,
  // welcome → index). `welcome` is terminal in the ownership regex, so a deeper `welcome/...`
  // path is `embedded` there — fall through to the Ember hand-off below to keep them in agreement.
  if (first === undefined || (first === WELCOME_SEGMENT && rest.length === 0)) {
    return { kind: 'list', tab: 'deliveries' };
  }
  const tab = tabForSegment(first);
  if (tab == null) {
    return { kind: 'ember' };
  }
  if (rest.length === 0) {
    return { kind: 'list', tab };
  }
  if (tab === 'deliveries' && rest.length === 1) {
    return { kind: 'detail', tab, id: rest[0] };
  }
  return { kind: 'ember' };
}

/** The resolved routing decision for an Outbox splat — the React-free core, so it unit-tests directly. */
type OutboxResolution =
  | { kind: 'ember' }
  | { kind: 'redirect'; splat: string }
  | { kind: 'list'; tab: OutboxTab }
  | { kind: 'detail'; tab: OutboxTab; id: string };

/**
 * Resolve an incoming (`segment`, `splat`) against the canonical segment: hand off to Ember,
 * redirect to the canonical URL, or render an already-canonical tab list / detail. The `redirect`
 * splat covers the segment swap, a legacy tab name (`outbox/sent` → `outbox/deliveries`), and the
 * index / `/welcome` landing paths (→ the default Sent tab).
 */
export function resolveOutboxRoute(
  segment: OutboxSegment,
  splat: string,
  canonicalSegment: OutboxSegment,
): OutboxResolution {
  const view = classifyOutboxPath(splat);
  if (view.kind === 'ember') {
    return { kind: 'ember' };
  }
  const canonicalTab = tabSegmentFor(canonicalSegment, view.tab);
  const canonicalSplat = view.kind === 'detail' ? `${canonicalTab}/${view.id}` : canonicalTab;
  if (segment !== canonicalSegment || normalizeSplat(splat) !== canonicalSplat) {
    return { kind: 'redirect', splat: canonicalSplat };
  }
  return view;
}

type OutboxRouteViewProps = {
  mount: OutboxMount;
  workspaceId: WorkspaceIdRouteParamInput;
  /** The path after the mount's segment (e.g. `sent`, `drafts`, `sent/<id>`). */
  splat: string;
};

/**
 * Gate + render for the Outbox subtree. Migrated to React: the Deliveries/Sent tab (list + a single
 * `:id` detail) and the Drafts tab (list only), plus the index + `/welcome` landing paths, which
 * redirect to the default tab. These render only on the Hydra mount; the old `journeys` mounts
 * forward to it. The draft detail/edit and any deeper path are Ember's: the `journeys` mounts
 * render the embedded Ember host for them, and the Hydra mount sends them to the `journeys` URL.
 */
export function OutboxRouteView({ mount, workspaceId: workspaceIdParam, splat }: OutboxRouteViewProps) {
  const router = useRouter();
  const location = useLocation();
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = normalizeWorkspaceIdRouteParam(workspaceIdParam);
  const canonicalSegment: OutboxSegment = 'message_activity';

  // The migrated pages read the workspace via `requireWorkspaceId()` (a URL parse) that can't
  // resolve the `/workspaces/last` sentinel, so resolve it to the real id before they render.
  // A no-op for numeric workspace URLs.
  if (workspaceId === 'last') {
    return currentWorkspace == null ? null : (
      <Navigate
        to={MOUNT_SPLAT_ROUTES[mount]}
        params={{ workspaceId: String(currentWorkspace.id), _splat: splat }}
        search
        hash
        replace
      />
    );
  }

  const resolution = resolveOutboxRoute(segmentForMount(mount), splat, canonicalSegment);
  if (resolution.kind === 'ember') {
    if (mount !== 'hydra') {
      return <Journeys />;
    }

    // Ember owns this path, and Ember has no route for the Hydra URL, so send it to the
    // `journeys` URL. The exit is to an Ember-owned route, so it goes through SafeNavigate.
    const emberHref = router.buildLocation({
      to: EMBER_SPLAT_ROUTE,
      params: { workspaceId, _splat: normalizeSplat(splat) },
    }).href;
    return (
      <SafeNavigate
        to={`${emberHref}${location.searchStr}${toHashSuffix(location.hash)}`}
        options={{ shouldReplaceHistoryEntry: true }}
      />
    );
  }

  // Redirect to the canonical Hydra URL (an old `journeys` mount, the segment swap, a legacy tab
  // name, index, welcome), preserving `search` + `hash` across the bounce.
  if (resolution.kind === 'redirect' || mount !== 'hydra') {
    const targetSplat = resolution.kind === 'redirect' ? resolution.splat : normalizeSplat(splat);
    return <Navigate to={HYDRA_SPLAT_ROUTE} params={{ workspaceId, _splat: targetSplat }} search hash replace />;
  }

  const splatHref = (targetSplat: string) =>
    router.buildLocation({
      to: splatRouteFor(targetSplat),
      params: { workspaceId, _splat: targetSplat },
    }).href;

  if (resolution.kind === 'detail') {
    // Each related row links to its own delivery: a drafted sibling routes to the
    // Ember-owned draft path, everything else to the React detail (Ember picked
    // `draft.index` vs `outbox.deliveries.delivery` the same way).
    const relatedDeliveryHref = (related: Delivery) => {
      const tab = getDeliveryState(related) === 'drafted' ? DRAFTS_TAB : OUTBOX_SEGMENTS[canonicalSegment].sentTab;
      return splatHref(`${tab}/${related.id}`);
    };
    // Ember's `outbox` + `outbox.deliveries` route crumbs, both pointing at the Sent-tab list
    // (the delivery-subject crumb is appended by the detail page once it loads).
    const listHref = splatHref(tabSegmentFor(canonicalSegment, 'deliveries'));
    const breadcrumbs: BreadcrumbItem[] = [
      { id: 'outbox', text: 'Message activity', href: listHref },
      { id: 'outbox-deliveries', text: 'Sent', href: listHref },
    ];
    return (
      <AppErrorBoundary resetKeys={[resolution.id]}>
        <RumPageRendered>
          <DeliveryDetailPage
            deliveryId={resolution.id}
            getDeliveryHref={relatedDeliveryHref}
            breadcrumbs={breadcrumbs}
          />
        </RumPageRendered>
      </AppErrorBoundary>
    );
  }

  const tabHref = (target: OutboxTab) => splatHref(tabSegmentFor(canonicalSegment, target));
  const getDeliveryHref = (delivery: Delivery) =>
    splatHref(`${tabSegmentFor(canonicalSegment, resolution.tab)}/${delivery.id}`);

  return (
    <RumPageRendered>
      <OutboxPage tab={resolution.tab} tabHref={tabHref} getDeliveryHref={getDeliveryHref} />
    </RumPageRendered>
  );
}
